/**
 * 수입 세금 계산기 (?page=taxcalc) — CS 문의(2026-06-26) 대응.
 * 과세가격(물품금액×과세환율 + 운임 + 기타가산) → 관세 → 개별소비세·교육세·농특세
 * → 부가세 사슬 계산. 재료: 주간 과세환율(/exchange-rate) + HS 세율/내국세(/hsk-detail).
 * 특송/과세운임 계산기에서 "이 운임으로 세금 계산" 연동(sessionStorage taxcalc_prefill).
 */
import React, { useState, useEffect, useMemo, useRef } from "react";

import ApiPortalLayout from "./ApiPortalLayout";

/** 협정·원산지 등 조건 없이 신고할 수 있는 세율 — 기본세율(A)·WTO협정세율(C). C1~C6(선택세율)·R·U·F… 는 조건부. */
const GENERAL_RATES = new Set(["A", "C", "W2"]);

const S = {
  page: { maxWidth: 880, color: "#1f2937",
    fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 26, fontWeight: 800, marginBottom: 4 },
  p: { fontSize: 13.5, color: "#4b5563", margin: "4px 0" },
  card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: "18px 20px", marginTop: 14 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 },
  input: { height: 44, padding: "0 12px", fontSize: 14, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box", width: "100%" },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 8,
    height: 44, padding: "0 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", display: "inline-flex", alignItems: "center",
    justifyContent: "center", boxSizing: "border-box" },
  ghost: { background: "#fff", color: "#111827", border: "1.5px solid #d1d5db",
    borderRadius: 8, height: 44, padding: "0 16px", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", boxSizing: "border-box" },
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
  row: { display: "flex", justifyContent: "space-between", padding: "7px 0",
    borderBottom: "1px solid #f3f4f6", fontSize: 14 },
};
const won = (n) => Math.round(n).toLocaleString("ko-KR") + "원";
const fmtDt = (d) => (d && d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}` : d || "");

export default function TaxCalcPage({ onNavigate, embedded = false }) {
  // 환율
  const [exch, setExch] = useState(null); // {week_start, rows}
  const [cur, setCur] = useState("USD");
  // 입력
  const [amount, setAmount] = useState("");
  const [freight, setFreight] = useState("");
  const [extra, setExtra] = useState("");
  // 관세율 — HS 조회 또는 직접 입력
  const [hsk, setHsk] = useState("");
  const [detail, setDetail] = useState(null); // hsk-detail 응답
  const [dutyOpts, setDutyOpts] = useState([]); // [{key,label,value}]
  const [dutySel, setDutySel] = useState(null); // key
  const [manualRate, setManualRate] = useState("");
  const [hsBusy, setHsBusy] = useState(false);
  const [hsErr, setHsErr] = useState(null);
  const [result, setResult] = useState(null);
  const lookedUp = useRef("");   // 마지막으로 조회한 10자리 — 같은 번호를 거듭 부르지 않는다

  useEffect(() => {
    fetch("/api/tools/exchange-rate?imexTp=2")
      .then((r) => (r.ok ? r.json() : null))
      .then(setExch)
      .catch(() => setExch(null));
    // 운임 계산기 연동 프리필
    try {
      const pre = JSON.parse(sessionStorage.getItem("taxcalc_prefill") || "null");
      if (pre?.freight) {
        setFreight(String(pre.freight));
        sessionStorage.removeItem("taxcalc_prefill");
      }
    } catch (e) { /* 무시 */ }
  }, []);

  const rate = useMemo(() => {
    const row = exch?.rows?.find((r) => r.curr_sgn === cur);
    return row ? Number(row.fxrt) : null;
  }, [exch, cur]);

  const lookupHs = async () => {
    const h = hsk.replace(/[^0-9]/g, "");
    if (h.length !== 10) { setHsErr("HS코드 10자리를 입력하세요."); return; }
    setHsBusy(true);
    setHsErr(null);
    setDetail(null);
    setDutyOpts([]);
    try {
      const r = await fetch(`/api/onto/hsk-detail?hsk=${h}&lang=kor`);
      if (!r.ok) throw new Error("HTTP " + r.status);
      const d = await r.json();
      setDetail(d);
      // rates_full 이 전체 세율의 원천 — A(기본세율)는 rates_grouped 에 없음
      const opts = [];
      for (const [code, g] of Object.entries(d.rates_full || {})) {
        const v = g && g.value;
        if (code === "U") continue;   // 북한산 — 세번 상세와 같이 보이지 않는다(문의 #75)
        if (v != null && Number.isFinite(Number(v))) {
          opts.push({ key: code, label: g.label || code, value: Number(v) });
        }
      }
      // 문의 #147(2026-10-07): 협정·원산지 같은 조건 없이 신고할 수 있는 세율은 기본세율(A)·WTO협정세율(C) 둘 —
      // 그중 낮은 것을 먼저 고르고(예: 3919100000 A 8% · C 6.5% → 6.5%), 협정·특수 세율은 아래에 따로 보인다.
      for (const o of opts) o.general = GENERAL_RATES.has(o.key);
      const rank = (o) => (o.general ? 0 : /^F/.test(o.key) ? 2 : 1);
      opts.sort((a, b) => rank(a) - rank(b) || a.value - b.value);
      setDutyOpts(opts);
      // 농림축산물 양허관세는 추천(W1)이 조건부, 미추천(W2)이 조건 없이 내는 세율이라 있으면 W2 를 먼저 고른다
      // (예: 1006.10-0000 — 기본세율·WTO 세율이 W2 보다 낮아 보여도 신고는 W2 로 한다, 문의 #147 후속)
      const lowest = opts.find((o) => o.key === "W2")
        || opts.filter((o) => o.general).sort((a, b) => a.value - b.value)[0] || opts[0];
      setDutySel(lowest ? lowest.key : null);
      lookedUp.current = h;
    } catch (e) {
      setHsErr("세율 조회 실패 — HS코드를 확인하세요.");
    } finally { setHsBusy(false); }
  };

  // 문의 #147: [세율 조회]를 누르지 않아도 10자리가 채워지면 바로 조회한다(잠깐 쉬었다가 — 타자 중 연달아 부르지 않게)
  useEffect(() => {
    const h = hsk.replace(/[^0-9]/g, "");
    if (h.length !== 10 || h === lookedUp.current) return undefined;
    const t = setTimeout(() => { lookupHs(); }, 350);
    return () => clearTimeout(t);
  }, [hsk]); // eslint-disable-line react-hooks/exhaustive-deps

  const excises = useMemo(() => {
    // % 개소세만 자동 (종량세·기준가격 조건부는 안내만)
    const items = detail?.internal_tax?.items || [];
    return items.map((x) => {
      const r = String(x.flex_rate || x.base_rate || "");
      const pct = r.match(/^([\d.]+)\s*%$/);
      return { ...x, pct: pct ? Number(pct[1]) : null };
    });
  }, [detail]);

  const calc = () => {
    setResult(null);
    const amt = parseFloat(String(amount).replace(/,/g, ""));
    const fr = parseFloat(String(freight).replace(/,/g, "")) || 0;
    const ex = parseFloat(String(extra).replace(/,/g, "")) || 0;
    if (!Number.isFinite(amt) || amt <= 0) { setHsErr("물품금액을 입력하세요."); return; }
    if (!rate) { setHsErr("과세환율을 불러오지 못했습니다."); return; }
    const dutyOpt = dutyOpts.find((o) => o.key === dutySel);
    const dutyRate = dutyOpt ? dutyOpt.value : parseFloat(manualRate);
    if (!Number.isFinite(dutyRate)) {
      setHsErr("관세율을 조회·선택하거나 직접 입력하세요.");
      return;
    }
    setHsErr(null);
    const cif = amt * rate + fr + ex;
    const duty = cif * (dutyRate / 100);
    // % 개소세 자동 합산(조건부·종량세 제외)
    let excise = 0, edu = 0, farm = 0;
    const applied = [];
    for (const x of excises) {
      if (x.pct == null || x.price_floor) continue; // 종량·기준가격 조건부 제외
      const e = (cif + duty) * (x.pct / 100);
      excise += e;
      const eduPct = Number(String(x.edu_rate || "").replace("%", "")) || 0;
      const farmPct = Number(String(x.farm_rate || "").replace("%", "")) || 0;
      edu += e * (eduPct / 100);
      farm += e * (farmPct / 100);
      applied.push(`${x.tax_label} ${x.target_name} ${x.pct}%`);
    }
    // 세액은 10원 미만 절사(1원 단위 0). 합계는 절사된 세액의 합
    const t10 = (n) => Math.floor(n / 10) * 10;
    const dutyT = t10(duty), exciseT = t10(excise), eduT = t10(edu), farmT = t10(farm);
    const vatT = t10((cif + dutyT + exciseT + eduT + farmT) * 0.1);
    setResult({
      rate, cif, dutyRate, dutyLabel: dutyOpt ? dutyOpt.label : `직접입력 ${dutyRate}%`,
      duty: dutyT, excise: exciseT, edu: eduT, farm: farmT, vat: vatT, applied,
      total: dutyT + exciseT + eduT + farmT + vatT,
      skipped: excises.filter((x) => x.pct == null || x.price_floor),
    });
  };

  return (
    <ApiPortalLayout active="taxcalc" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>수입 세금 계산기</h1>
        <p style={S.p}>
          물품금액·운임·관세율로 <b>관세 → 개별소비세·교육세·농특세 → 부가세</b>를 한 번에
          계산합니다. 과세환율은 관세청 주간 고시가 자동 적용됩니다
          {exch && <> (적용 주 <b>{fmtDt(exch.week_start)}</b>)</>}.
        </p>

        <div style={S.card}>
          {/* 휴대폰: 칸을 한 줄씩 쌓는다(좁은 칸에 이름이 한 글자씩 세로로 쌓이던 것, 2026-10-02) */}
          <style>{"@media (max-width: 640px) { .tc-grid { grid-template-columns: 1fr !important; } }"}</style>
          {/* 1행: 통화·물품금액·환율 */}
          <div className="tc-grid" style={{ display: "grid", gridTemplateColumns: "110px 1fr 170px",
            gap: 12, alignItems: "end" }}>
            <div>
              <label style={S.label}>통화</label>
              <select value={cur} onChange={(e) => setCur(e.target.value)} style={S.input}>
                {(exch?.rows || [{ curr_sgn: "USD" }]).map((r) => (
                  <option key={r.curr_sgn} value={r.curr_sgn}>{r.curr_sgn}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={S.label}>물품금액 (인보이스 금액)</label>
              <input type="number" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="1000"
                style={S.input} />
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", paddingBottom: 12 }}>
              과세환율 {rate ? <b>{rate.toLocaleString()}원/{cur}</b> : "…"}
            </div>
          </div>
          {/* 2행: 운임·기타 */}
          <div className="tc-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <div>
              <label style={S.label}>운임·보험료 (KRW)</label>
              <input type="number" min="0" value={freight}
                onChange={(e) => setFreight(e.target.value)} placeholder="0" style={S.input} />
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
                운임을 모르면{" "}
                <a href="/?page=freight"
                  onClick={(e) => { e.preventDefault(); onNavigate("freight"); }}>
                  운임 계산기
                </a>
                에서 계산 후 "세금 계산" 버튼으로 가져올 수 있습니다.
              </div>
            </div>
            <div>
              <label style={S.label}>기타 가산금액 (KRW)</label>
              <input type="number" min="0" value={extra}
                onChange={(e) => setExtra(e.target.value)} placeholder="0" style={S.input} />
            </div>
          </div>
          {/* 3행: HS·관세율 */}
          <div className="tc-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr 130px",
            gap: 12, alignItems: "end", marginTop: 12 }}>
            <div>
              <label style={S.label}>HS코드 (10자리) — 세율·내국세 자동 조회</label>
              <input value={hsk} onChange={(e) => setHsk(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") lookupHs(); }}
                placeholder="8501101000" style={S.input} />
            </div>
            <button style={S.ghost} onClick={lookupHs} disabled={hsBusy}>
              {hsBusy ? "조회 중…" : "세율 조회"}
            </button>
            <div>
              <label style={S.label}>
                {dutyOpts.length ? "적용 세율 선택" : "또는 관세율 직접 입력 (%)"}
              </label>
              {dutyOpts.length ? (
                <select value={dutySel || ""} onChange={(e) => setDutySel(e.target.value)}
                  style={S.input} data-testid="taxcalc-duty-select">
                  <optgroup label="일반 적용 (조건 없음)">
                    {dutyOpts.filter((o) => o.general).map((o) => (
                      <option key={o.key} value={o.key}>{o.label} — {o.value}%</option>
                    ))}
                  </optgroup>
                  <optgroup label="협정·특수 세율 (원산지·요건 충족 시)">
                    {dutyOpts.filter((o) => !o.general).map((o) => (
                      <option key={o.key} value={o.key}>{o.label} — {o.value}%</option>
                    ))}
                  </optgroup>
                </select>
              ) : (
                <input type="number" min="0" step="0.1" value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)} placeholder="8"
                  style={S.input} />
              )}
            </div>
            <button style={S.btn} onClick={calc}>세액 계산</button>
          </div>
          {dutyOpts.length > 0 && <SpecialRates opts={dutyOpts} selected={dutySel} onPick={setDutySel} />}
          {dutyOpts.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#9ca3af" }}>
              {detail?.name_ko && <>품명: {detail.name_ko} · </>}
              직접 입력으로 바꾸려면{" "}
              <span style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => { setDutyOpts([]); setDutySel(null); }}>여기</span>
            </div>
          )}
          {hsErr && <div style={{ marginTop: 8, fontSize: 13, color: "#b91c1c" }}>{hsErr}</div>}

          {result && (
            <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 10,
              padding: "14px 18px", background: "#f8fafc" }}>
              <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 6 }}>
                과세가격(CIF) = 물품금액 × {result.rate.toLocaleString()}원 + 운임 + 가산금액
              </div>
              <div style={S.row}><span>과세가격 (CIF)</span><b>{won(result.cif)}</b></div>
              <div style={S.row}>
                <span>관세 ({result.dutyLabel} {result.dutyRate}%)</span>
                <b>{won(result.duty)}</b>
              </div>
              {result.excise > 0 && (
                <>
                  <div style={S.row}>
                    <span>개별소비세 ({result.applied.join(", ")})</span>
                    <b>{won(result.excise)}</b>
                  </div>
                  {result.edu > 0 && (
                    <div style={S.row}><span>교육세</span><b>{won(result.edu)}</b></div>
                  )}
                  {result.farm > 0 && (
                    <div style={S.row}><span>농어촌특별세</span><b>{won(result.farm)}</b></div>
                  )}
                </>
              )}
              <div style={S.row}><span>부가가치세 (10%)</span><b>{won(result.vat)}</b></div>
              <div style={{ ...S.row, borderBottom: "none", fontSize: 16, paddingTop: 10 }}>
                <span style={{ fontWeight: 800 }}>총 세액</span>
                <span style={{ fontWeight: 800 }}>{won(result.total)}</span>
              </div>
              {result.skipped.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#664d03",
                  background: "#fef7e0", borderRadius: 6, padding: "6px 10px" }}>
                  ⚠ 자동 미반영: {result.skipped.map((x) =>
                    `${x.target_name}(${x.price_floor ? "기준가격 조건부" : "종량세"})`).join(", ")}
                  — 해당 시 별도 확인 필요
                </div>
              )}
            </div>
          )}
        </div>

        <div style={S.note}>
          참고용 계산입니다 — 소액면세(목록통관·150불 등)·간이세율(
          <a href="/?audit=simplified-rate" style={{ color: "#2563eb" }}>
            안내
          </a>
          )·기준가격 조건부 개별소비세· 종량세·감면은 반영되지 않습니다. 신고
          세액은 관세사 확인을 권장합니다.
        </div>
      </div>
    </ApiPortalLayout>
  );
}

/** 협정·특수 세율 — 선택 상자 아래에 낮은 순으로 따로 보인다(문의 #147). 누르면 그 세율로 계산한다. */
function SpecialRates({ opts, selected, onPick }) {
  const [all, setAll] = useState(false);
  const general = opts.filter((o) => o.general).sort((a, b) => a.value - b.value);
  const special = opts.filter((o) => !o.general).sort((a, b) => a.value - b.value);
  const shown = all ? special : special.slice(0, 8);
  const pick = (o) => (
    <button key={o.key} type="button" onClick={() => onPick(o.key)} aria-pressed={selected === o.key}
      style={{ border: "1px solid " + (selected === o.key ? "#2563eb" : "#e5e7eb"), background: "transparent", borderRadius: 6,
        padding: "2px 8px", fontSize: 12, cursor: "pointer", color: selected === o.key ? "#2563eb" : "#374151", whiteSpace: "nowrap" }}>
      {o.label} <b>{o.value}%</b>
    </button>
  );
  return (
    <div data-testid="taxcalc-rates" style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 12, color: "#6b7280" }}>
      {general.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span>일반 적용(조건 없음) — 낮은 세율 먼저:</span>{general.map(pick)}
        </div>
      )}
      {special.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span>협정·특수 세율(원산지·요건 충족 시):</span>{shown.map(pick)}
          {special.length > shown.length && (
            <button type="button" onClick={() => setAll(true)}
              style={{ border: 0, background: "transparent", color: "#6b7280", textDecoration: "underline", cursor: "pointer", fontSize: 12 }}>
              외 {special.length - shown.length}개 더 보기
            </button>
          )}
        </div>
      )}
    </div>
  );
}
